import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { IMAGES, ARTICLES } from "@/data/site";

export default function BlogPost() {
  const { id } = useParams();
  const article = ARTICLES.find((a) => a.id === id);
  const others = ARTICLES.filter((a) => a.id !== id).slice(0, 2);

  if (!article) {
    return (
      <div data-testid="page-blog-post" className="pt-40 pb-24 text-center">
        <h1 className="font-display text-3xl font-bold text-slate-900">
          Artikel tidak ditemukan
        </h1>
        <Link
          to="/artikel"
          className="mt-6 inline-flex items-center gap-2 text-emerald-600 font-semibold"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Articles
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="page-blog-post">
      <PageHero
        crumb={article.category}
        eyebrow={article.category}
        title={article.title}
        image={IMAGES[article.img]}
      />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center gap-5 text-sm text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-4 w-4" /> {article.date}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-4 w-4" /> {article.read} baca
          </span>
        </div>

        <img
          src={IMAGES[article.img]}
          alt={article.title}
          className="mt-8 w-full rounded-3xl object-cover shadow-lg"
        />

        <div className="mt-10 space-y-6">
          {article.body.map((p, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-lg leading-relaxed text-slate-700"
            >
              {p}
            </motion.p>
          ))}
        </div>

        <div className="mt-12 rounded-3xl bg-gradient-to-br from-blue-900 to-emerald-700 p-8 text-white">
          <h3 className="font-display text-xl font-bold">
            Interested in implementing solar panels at your facility?
          </h3>
          <p className="mt-2 text-blue-100">
            The AJE team is ready to assist you from feasibility studies to commissioning.
          </p>
          <Link
            to="/kontak"
            data-testid="blogpost-cta"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-blue-900 transition-transform hover:-translate-y-0.5"
          >
            Free Consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <Link
          to="/artikel"
          data-testid="blogpost-back"
          className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-900"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Articles
        </Link>
      </article>

      {/* RELATED */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-slate-900">
            Artikel Lainnya
          </h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            {others.map((a) => (
              <Link
                key={a.id}
                to={`/artikel/${a.id}`}
                data-testid={`related-article-${a.id}`}
                className="group flex gap-4 rounded-2xl bg-white p-4 shadow-sm aje-card-hover"
              >
                <img
                  src={IMAGES[a.img]}
                  alt={a.title}
                  className="h-24 w-24 shrink-0 rounded-xl object-cover"
                />
                <div>
                  <h3 className="mt-1 font-display font-bold leading-snug text-slate-900 transition-colors group-hover:text-blue-900">
                    {a.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
